/**
 * Plan 13-02 — RejectDialog
 *
 * Dialog for rejecting a pending entry. Requires:
 *  - reason: one of the 5 RejectionReason values (required)
 *  - remarks: min 10 chars (required) — T-13-11 client-side UX; server re-validates.
 *
 * Uses react-hook-form + Zod via useZodForm.
 */

import type { JSX } from 'react';
import { Controller } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useRejectMutation } from '@/hooks/useLogsheetCheckerQueries';
import { useZodForm } from '@/lib/forms/useZodForm';
import { toast } from '@/lib/toast';
import { REJECTION_REASON_LABELS, type RejectionReason } from '@/types/logsheet-checker';

const VALID_REASONS = [
  'IncorrectKpi',
  'MissingDoc',
  'WrongMachine',
  'DateMismatch',
  'Other',
] as const satisfies readonly RejectionReason[];

const rejectSchema = z.object({
  reason: z.enum(VALID_REASONS, { required_error: 'Reason is required' }),
  remarks: z.string().trim().min(10, 'Remarks must be at least 10 characters').max(500),
});

export interface RejectDialogProps {
  readonly open: boolean;
  readonly entryId: number;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

export function RejectDialog({
  open,
  entryId,
  onClose,
  onSuccess,
}: RejectDialogProps): JSX.Element {
  const form = useZodForm({
    schema: rejectSchema,
    defaultValues: { remarks: '' },
  });

  const mutation = useRejectMutation();

  function handleClose(): void {
    form.reset();
    mutation.reset();
    onClose();
  }

  const onSubmit = form.handleSubmit((values) => {
    mutation.mutate(
      {
        id: entryId,
        body: {
          reason: values.reason,
          remarks: values.remarks.trim(),
        },
      },
      {
        onSuccess: () => {
          toast.success('Entry rejected');
          form.reset();
          onSuccess();
        },
        onError: (err) => {
          toast.error('Could not reject entry', {
            description: err.detail ?? err.message,
          });
        },
      },
    );
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose();
      }}
    >
      <DialogContent data-testid="reject-dialog">
        <DialogHeader>
          <DialogTitle>Reject Entry #{entryId}</DialogTitle>
          <DialogDescription>
            Reject this logsheet entry. The Maker will be notified. Provide a clear reason and
            remarks (minimum 10 characters).
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} noValidate>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label htmlFor="reject-reason">Reason *</Label>
              <Controller
                name="reason"
                control={form.control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={(v) => {
                      if (VALID_REASONS.includes(v as RejectionReason)) {
                        field.onChange(v);
                      }
                    }}
                  >
                    <SelectTrigger id="reject-reason" data-testid="reject-dialog-reason">
                      <SelectValue placeholder="Select a reason…" />
                    </SelectTrigger>
                    <SelectContent>
                      {VALID_REASONS.map((r) => (
                        <SelectItem key={r} value={r} data-testid={`reject-dialog-reason-${r}`}>
                          {REJECTION_REASON_LABELS[r]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {form.formState.errors.reason !== undefined && (
                <p className="text-xs text-destructive">{form.formState.errors.reason.message}</p>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="reject-remarks">Remarks * (min 10 characters)</Label>
              <Textarea
                id="reject-remarks"
                placeholder="Describe the issue in detail…"
                rows={4}
                data-testid="reject-dialog-remarks"
                {...form.register('remarks')}
              />
              {form.formState.errors.remarks !== undefined && (
                <p className="text-xs text-destructive">{form.formState.errors.remarks.message}</p>
              )}
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={mutation.isPending}
              data-testid="reject-dialog-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={mutation.isPending}
              data-testid="reject-dialog-submit"
            >
              {mutation.isPending ? 'Rejecting…' : 'Reject'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
